/**
 * InjectionGuideCard — Per-medication injection guidance.
 *
 * Expandable sections for different injection types:
 * subcutaneous (belly), intramuscular (thigh).
 * Shown in BeforeTreatmentVariant and accessible from Care.
 */

import React, { useState } from "react";
import { View, Pressable } from "react-native";
import { Text } from "@src/packages/ui/Text/Text";
import { Syringe, ChevronDown, ChevronUp } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";

interface InjectionType {
  id: string;
  titleKey: string;
  subtitleKey: string;
  color: string;
  stepKeys: string[];
  tipKeys: string[];
}

const INJECTION_TYPES: InjectionType[] = [
  {
    id: "subcutaneous",
    titleKey: "medication_factsheet.guide_subq_title",
    subtitleKey: "medication_factsheet.guide_subq_subtitle",
    color: "#C4B5FD",
    stepKeys: [
      "medication_factsheet.guide_subq_step_1",
      "medication_factsheet.guide_subq_step_2",
      "medication_factsheet.guide_subq_step_3",
      "medication_factsheet.guide_subq_step_4",
      "medication_factsheet.guide_subq_step_5",
      "medication_factsheet.guide_subq_step_6",
    ],
    tipKeys: [
      "medication_factsheet.guide_subq_tip_1",
      "medication_factsheet.guide_subq_tip_2",
      "medication_factsheet.guide_subq_tip_3",
      "medication_factsheet.guide_subq_tip_4",
    ],
  },
  {
    id: "intramuscular",
    titleKey: "medication_factsheet.guide_im_title",
    subtitleKey: "medication_factsheet.guide_im_subtitle",
    color: "#F472B6",
    stepKeys: [
      "medication_factsheet.guide_im_step_1",
      "medication_factsheet.guide_im_step_2",
      "medication_factsheet.guide_im_step_3",
      "medication_factsheet.guide_im_step_4",
      "medication_factsheet.guide_im_step_5",
      "medication_factsheet.guide_im_step_6",
      "medication_factsheet.guide_im_step_7",
    ],
    tipKeys: [
      "medication_factsheet.guide_im_tip_1",
      "medication_factsheet.guide_im_tip_2",
      "medication_factsheet.guide_im_tip_3",
      "medication_factsheet.guide_im_tip_4",
    ],
  },
];

function InjectionTypeSection({ type }: { type: InjectionType }) {
  const t = useTranslations();
  const capturePress = usePressCapture("InjectionTypeSection");
  const [expanded, setExpanded] = useState(false);

  return (
    <View className="rounded-xl border border-white/5 bg-white/5">
      <Pressable
        onPress={() => {
          capturePress(t(type.titleKey), type.titleKey);
          setExpanded(!expanded);
        }}
        className="flex-row items-center justify-between px-4 py-3"
      >
        <View className="flex-1">
          <Text className="text-sm font-medium text-white">
            {t(type.titleKey)}
          </Text>
          <Text className="text-xs text-slate-500">{t(type.subtitleKey)}</Text>
        </View>
        {expanded ? (
          <ChevronUp size={16} color="rgba(255,255,255,0.3)" />
        ) : (
          <ChevronDown size={16} color="rgba(255,255,255,0.3)" />
        )}
      </Pressable>

      {expanded && (
        <View className="gap-3 border-t border-white/5 px-4 py-3">
          <View className="gap-1.5">
            <Text className="text-xs font-medium text-slate-300">
              {t("medication_factsheet.guide_steps_heading")}
            </Text>
            {type.stepKeys.map((stepKey, i) => (
              <View key={stepKey} className="flex-row gap-2">
                <Text className="text-xs text-slate-600" style={{ width: 16 }}>
                  {i + 1}.
                </Text>
                <Text className="flex-1 text-xs leading-4 text-slate-400">
                  {t(stepKey)}
                </Text>
              </View>
            ))}
          </View>

          <View className="gap-1.5">
            <Text className="text-xs font-medium text-slate-300">
              {t("medication_factsheet.guide_tips_heading")}
            </Text>
            {type.tipKeys.map((tipKey) => (
              <View key={tipKey} className="flex-row gap-2">
                <Text className="text-xs text-slate-600">{"•"}</Text>
                <Text className="flex-1 text-xs leading-4 text-slate-400">
                  {t(tipKey)}
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

export function InjectionGuideCard() {
  const t = useTranslations();
  return (
    <View className="gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
      <View className="flex-row items-center gap-2">
        <Syringe size={18} color="#C4B5FD" />
        <Text className="text-base font-semibold text-white">
          {t("medication_factsheet.guide_title")}
        </Text>
      </View>
      <Text className="text-xs text-slate-500">
        {t("medication_factsheet.guide_intro")}
      </Text>
      <View className="gap-2">
        {INJECTION_TYPES.map((type) => (
          <InjectionTypeSection key={type.id} type={type} />
        ))}
      </View>
    </View>
  );
}
